import { useEffect } from 'react';
import Loadable, { LoadingSpinner } from '@comeon/loadable';
import { Link } from '~/utils/navigation';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { H3 } from '@comeon/ui';
import { getZindexByType } from '~/utils/style-utils.ts';
import Blur from '../misc/blur.js';
import { showCookieBanner, setCookies, fetchCookieCategories } from './ducks/index.js';
import { ROUTE_COOKIE_POLICY } from '../router/constants.js';
import { COOKIE_CATEGORY, COOKIE_NECESSARY, getCookies } from '~/store/cookies.js';
import { Bigger, Smaller } from '~/components/layout/mediaquery';
import useTranslations from '~/components/translation/use-translations';
import Loading from '~/components/misc/loading';

const CookieButtons = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "cookie-settings-buttons" */
			'./cookie-buttons.js'
		),
	loading: LoadingSpinner,
	delay: 300
});

const CookieSettings = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "cookie-settings-buttons" */
			'./cookie-settings.js'
		),
	loading: LoadingSpinner,
	delay: 300
});

const CookieBannerWrapper = styled.div.attrs({
	'data-at': 'mobile-wrapper-cookie-banner'
})`
	background-color: var(--cookiebanner-wrapper-bg, rgba(var(--base-11), 1));
	position: fixed;
	bottom: 0;
	left: 0;
	box-shadow: 0px -7px 10px -4px rgba(0, 0, 0, 0.5);
	padding: 24px 16px 20px 16px;
	width: 100vw;
	z-index: ${getZindexByType('cookie-banner')};
	h4 {
		font: var(--h4-bold);
	}
`;

const CookieBannerText = styled.p`
	max-width: 1040px;
	margin: 4px 0;
	color: var(--cookie-banner-text);
`;

const CookiePolicyLink = styled(Link)`
	color: var(--cookie-policy-link, rgba(var(--base-02), 1));
	text-decoration: none;
`;

const CookieBlur = styled(Blur)`
	height: 100vh !important;
`;

const cookies = () => JSON.parse(getCookies(COOKIE_CATEGORY));

const TEXT_KEYS = [
	'COOKIE_BANNER_TITLE',
	'COOKIE_BANNER_TEXT',
	'COOKIE_LEARN_MORE_LINK_TEXT',
	'COOKIE_SETTINGS_LINK_TEXT',
	'COOKIE_OK_TEXT'
];

const CookieBanner = ({ children, showNewCookieBanner }) => {
	const dispatch = useDispatch();
	const displayCookieSettings = useSelector(state => state.cookieBanner.displayCookieSettings);
	const displayCookieBanner = useSelector(state => state.cookieBanner.displayCookieBanner);
	const { translations: t, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	useEffect(() => {
		if (showNewCookieBanner) {
			dispatch(fetchCookieCategories());
			const showBanner = !cookies() || !cookies()[COOKIE_NECESSARY];

			if (showBanner) {
				dispatch(showCookieBanner());
			} else {
				dispatch(setCookies());
			}
		}
	}, [dispatch, showNewCookieBanner]);

	if (isLoadingTranslations) {
		return <Loading />;
	}

	return (
		<>
			{displayCookieBanner && showNewCookieBanner && (
				<>
					<Smaller>
						<CookieBannerWrapper>
							<h4 className="cookie-banner-title">{t.COOKIE_BANNER_TITLE}</h4>
							<CookieBannerText>
								{t.COOKIE_BANNER_TEXT} &nbsp;
								<CookiePolicyLink href={ROUTE_COOKIE_POLICY} data-at="cookie-policy-link">
									{t.COOKIE_LEARN_MORE_LINK_TEXT}
								</CookiePolicyLink>
							</CookieBannerText>
							<CookieButtons cookieBanner />
						</CookieBannerWrapper>
					</Smaller>
					<Bigger>
						<CookieBannerWrapper>
							<H3 mt={2} mb={3} className="cookie-banner-title">
								{t.COOKIE_BANNER_TITLE}
							</H3>
							<CookieBannerText>
								{t.COOKIE_BANNER_TEXT} &nbsp;
								<CookiePolicyLink href={ROUTE_COOKIE_POLICY} data-at="cookie-policy-link">
									{t.COOKIE_LEARN_MORE_LINK_TEXT}
								</CookiePolicyLink>
							</CookieBannerText>
							<CookieButtons cookieBanner />
						</CookieBannerWrapper>
					</Bigger>
				</>
			)}
			{displayCookieSettings && <CookieSettings />}
			<CookieBlur enable={showNewCookieBanner && (displayCookieBanner || displayCookieSettings)}>
				{children}
			</CookieBlur>
		</>
	);
};

export default CookieBanner;
